import { parseUnits, formatUnits, ethers } from 'ethers';
import { formatGasPriceForDisplay, formatGasPriceForTransaction } from '../pages/Wallet/utils/gasUtils';

// Constants for gas settings
const MIN_GAS_PRICE_GWEI = '0.000001';

/**
 * Calculate the actual gas price to use based on the selected gas option
 * @param {Object} gasSettings - The gas settings object {gasOption, gasPrice}
 * @param {string} dynamicGasPrice - Current network gas price
 * @returns {string} - The calculated gas price in gwei
 */
export const calculateGasPriceFromSettings = (gasSettings, dynamicGasPrice) => {
  // Additional validation to log when gasSettings are missing
  if (!gasSettings) {
    console.error('No gas settings provided to calculateGasPriceFromSettings, using network price');
    return dynamicGasPrice || '3'; 
  }
  
  const { gasOption, gasPrice: customGasPrice } = gasSettings;
  const networkGasPrice = dynamicGasPrice || '3';  // Default if network gas price not available
  
  // Log inputs for debugging
  console.log('Calculating gas price with settings:', {
    gasOption,
    customGasPrice,
    networkGasPrice
  });
  
  // First calculate based on option
  let calculatedPrice;
  switch (gasOption) {
    case 'slow':
      calculatedPrice = (parseFloat(networkGasPrice) * 0.8).toString();
      break;
    case 'medium':
      calculatedPrice = networkGasPrice;
      break;
    case 'fast':
      calculatedPrice = (parseFloat(networkGasPrice) * 1.2).toString();
      break;
    case 'custom':
      calculatedPrice = customGasPrice || networkGasPrice;
      break;
    default:
      calculatedPrice = networkGasPrice;
  }
  
  // Ensure gas price is not below minimum
  if (parseFloat(calculatedPrice) < parseFloat(MIN_GAS_PRICE_GWEI)) {
    calculatedPrice = MIN_GAS_PRICE_GWEI;
  }
  
  console.log(`Gas calculation: option=${gasOption}, network=${networkGasPrice}, custom=${customGasPrice}, final=${calculatedPrice}`);
  return calculatedPrice;
};

/**
 * Prepare transaction with gas settings
 * @param {Object} transaction - Transaction object to prepare
 * @param {Object} gasSettings - The gas settings {gasOption, gasPrice}
 * @param {string} dynamicGasPrice - Current network gas price
 * @returns {Object} - Transaction with gas settings applied
 */
export const prepareTransactionWithGas = (transaction, gasSettings, dynamicGasPrice) => {
  // Calculate gas price based on settings and network conditions
  const gasPriceGwei = calculateGasPriceFromSettings(gasSettings, dynamicGasPrice);
  
  // Format to consistent format for transaction
  const formattedGasPriceGwei = formatGasPriceForTransaction(gasPriceGwei);
  
  // Convert to wei units for the transaction
  const gasPriceWei = parseUnits(formattedGasPriceGwei, "gwei");
  
  // Debug log to verify gas price is correct before applying
  console.log(`Setting transaction gas price to ${formattedGasPriceGwei} gwei (${gasPriceWei.toString()} wei)`);
  
  // Apply gas price to transaction
  return {
    ...transaction,
    gasPrice: gasPriceWei
  };
};

/**
 * Format gas price for display with appropriate decimal places
 * @param {string|number} gweiValue - Gas price in gwei
 * @returns {string} - Formatted gas price for display
 */
export const formatGasPrice = formatGasPriceForDisplay;

// IMPORTANT: This is the key function that sends transactions with proper gas settings
export const sendTransaction = async (provider, transaction, gasSettings, dynamicGasPrice) => {
  try {
    // Log all inputs for debugging
    console.log('sendTransaction inputs:', {
      transactionType: transaction.type || 'normal',
      to: transaction.to,
      value: transaction.value?.toString(),
      gasSettings,
      dynamicGasPrice
    });
    
    // Apply gas settings
    const txWithGas = prepareTransactionWithGas(transaction, gasSettings, dynamicGasPrice);
    
    // Log what's being sent (for debugging)
    console.log('Sending transaction with gas price:', {
      gweiValue: formatUnits(txWithGas.gasPrice, 'gwei'),
      weiValue: txWithGas.gasPrice.toString()
    });
    
    // Send the transaction
    const txResponse = await provider.sendTransaction(txWithGas);
    console.log('Transaction sent successfully:', txResponse.hash);
    return txResponse;
  } catch (error) {
    console.error("Transaction failed:", error);
    throw error;
  }
};

/**
 * Safely parse numeric strings to avoid "too many decimals" errors
 * @param {string} value - The string value to parse
 * @param {string} unit - The unit to parse as (e.g., 'gwei', 'ether')
 * @returns {bigint} - A safely parsed BigInt value
 */
export const safeParseUnits = (value, unit = 'gwei') => {
  try {
    // Try the standard parsing first
    return parseUnits(value, unit);
  } catch (error) {
    // If we get a "too many decimals" error, trim the decimals
    if (
      error.code === 'NUMERIC_FAULT' && 
      error.fault === 'underflow' && 
      typeof value === 'string'
    ) {
      console.log(`Handling numeric underflow for value: ${value}`);
      
      // Trim the value to fewer decimals
      const parts = value.split('.');
      if (parts.length === 2) {
        // Limit decimals to a reasonable number (9 is usually safe for gwei)
        const maxDecimals = unit === 'gwei' ? 9 : 6;
        const trimmedValue = `${parts[0]}.${parts[1].substring(0, maxDecimals)}`;
        console.log(`Trimmed value: ${trimmedValue}`);
        return parseUnits(trimmedValue, unit);
      }
    }
    
    // Re-throw any other errors
    throw error;
  }
};